import React from "react";
import { PHASE_TASKS } from "@/lib/phases";
import { Hand, Radio, Flashlight, ArrowDownToLine, Footprints, DoorOpen, ShieldAlert } from "lucide-react";

function Btn({ children, ...props }) {
  return (
    <button className="gold-btn" style={{ padding: "10px 20px", borderRadius: 4, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }} {...props}>
      {children}
    </button>
  );
}

export default function TaskPrompt({
  phase,
  pov,
  enabled,
  emitCount,
  emitsNeeded,
  aligned,
  pullPct,
  descentActive,
  aimNorm,
  handlers,
}) {
  const cfg = PHASE_TASKS[phase];
  if (!cfg) return null;
  const task = cfg[pov];

  let control = null;
  if (phase === "impact") {
    // Actionable immediately — the player never has to wait for the narration to finish.
    control = (
      <Btn data-testid="task-impact-button" onClick={handlers.onImpact}>
        <ShieldAlert size={16} /> {task.label}
      </Btn>
    );
  } else if (phase === "acoustic") {
    if (pov === "stanley") {
      control = (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
          <Btn data-testid="task-emit-button" disabled={!enabled} onClick={handlers.onEmit}>
            <Radio size={16} /> {task.label}
          </Btn>
          <div style={{ display: "flex", gap: 6 }}>
            {new Array(emitsNeeded).fill(0).map((_, i) => (
              <span key={i} style={{ width: 26, height: 4, borderRadius: 2, background: i < emitCount ? "#f2c85b" : "rgba(255,255,255,0.15)" }} />
            ))}
          </div>
          <span style={{ fontSize: 11, color: "#7e8594" }}>Strike the drywall to project sound rings ({emitCount}/{emitsNeeded})</span>
        </div>
      );
    } else {
      control = (
        <Btn data-testid="task-triangulate-button" disabled={!enabled} onClick={handlers.onTriangulate}>
          <Radio size={16} /> {task.label}
        </Btn>
      );
    }
  } else if (phase === "flashlight") {
    control = (
      <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center", width: "100%", maxWidth: 340 }}>
        {pov === "brian" && (
          <div style={{ width: "100%" }}>
            <input
              data-testid="flashlight-aim-slider"
              type="range"
              min="0"
              max="100"
              value={Math.round(aimNorm * 100)}
              onChange={(e) => handlers.onAim(Number(e.target.value) / 100)}
              disabled={!enabled}
              style={{ width: "100%", accentColor: aligned ? "#4cd07a" : "#c9a24b" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#7e8594" }}>
              <span>Sweep beam</span>
              <span style={{ color: aligned ? "#4cd07a" : "#7e8594" }}>{aligned ? "● LOCKED" : "seeking…"}</span>
            </div>
          </div>
        )}
        <Btn data-testid="task-lock-button" disabled={!enabled} onClick={handlers.onLock}>
          <Flashlight size={16} /> {task.label}
        </Btn>
      </div>
    );
  } else if (phase === "wristlock") {
    control = (
      <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center", width: "100%", maxWidth: 320 }}>
        <button
          data-testid="rescue-wrist-lock-action-btn"
          className="gold-btn"
          disabled={!enabled}
          onPointerDown={handlers.onPullStart}
          onPointerUp={handlers.onPullEnd}
          onPointerLeave={handlers.onPullEnd}
          style={{ padding: "16px 30px", borderRadius: 4, fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 9, userSelect: "none", touchAction: "none" }}
        >
          <Hand size={18} /> {task.label}
        </button>
        <div style={{ width: "100%", height: 8, background: "rgba(255,255,255,0.1)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: `${pullPct * 100}%`, height: "100%", background: "linear-gradient(90deg,#c9a24b,#4cd07a)", transition: "width 0.05s linear" }} />
        </div>
        <span style={{ fontSize: 11, color: "#7e8594" }}>Press & hold to lock wrist-to-wrist and pull</span>
      </div>
    );
  } else if (phase === "stairwell") {
    control = (
      <Btn data-testid="task-stairwell-button" disabled={!enabled} onClick={handlers.onChooseStairwell}>
        <ArrowDownToLine size={16} /> {task.label}
      </Btn>
    );
  } else if (phase === "descent") {
    control = (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
        <button
          data-testid="task-descend-button"
          className="gold-btn"
          disabled={!enabled}
          onPointerDown={handlers.onDescendStart}
          onPointerUp={handlers.onDescendEnd}
          onPointerLeave={handlers.onDescendEnd}
          style={{ padding: "16px 34px", borderRadius: 4, fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 9, userSelect: "none", touchAction: "none", filter: descentActive ? "brightness(1.15)" : "none" }}
        >
          <Footprints size={18} /> {task.label}
        </button>
        <span style={{ fontSize: 11, color: "#7e8594" }}>Hold to descend · release to catch your breath</span>
      </div>
    );
  } else if (phase === "exit") {
    control = (
      <Btn data-testid="task-exit-button" disabled={!enabled} onClick={handlers.onExit}>
        <DoorOpen size={16} /> {task.label}
      </Btn>
    );
  }

  return (
    <div
      data-testid="task-prompt"
      className="hud-panel tab-rise task-prompt-panel"
      style={{ padding: "12px 16px", borderRadius: 4, maxWidth: 360, pointerEvents: "auto", textAlign: "center" }}
    >
      <div style={{ fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "#c9a24b", marginBottom: 5 }}>
        {cfg.title}
      </div>
      <p style={{ margin: "0 0 10px", fontSize: 11.5, color: "#c2c6cf", lineHeight: 1.45 }}>{cfg.narration}</p>
      <div style={{ display: "flex", justifyContent: "center" }}>{control}</div>
      {phase === "impact" && !enabled && (
        <div data-testid="impact-skip-hint" style={{ marginTop: 8, fontSize: 10, color: "#8f96a3", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Skip to action anytime · testimony keeps playing
        </div>
      )}
      {phase !== "impact" && !enabled && (
        <div style={{ marginTop: 10, fontSize: 10, color: "#5a606c", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          Listening to testimony…
        </div>
      )}
    </div>
  );
}
